import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { TenantProvider } from './contexts/TenantContext'
import { Layout } from './components/layout/Layout'
import { LandingPage } from './pages/public/LandingPage'
import { EsportsLandingPage } from './pages/public/EsportsLandingPage'
import { RacketLandingPage } from './pages/public/RacketLandingPage'
import { LoginPage } from './pages/auth/LoginPage'
import { DashboardPage } from './pages/admin/DashboardPage'
import { ClubSettingsPage } from './pages/admin/ClubSettingsPage'
import { TeamsPage } from './pages/admin/TeamsPage'
import { PlayersPage } from './pages/admin/PlayersPage'
import { DivisionsPage } from './pages/admin/DivisionsPage'
import { MatchesPage } from './pages/admin/MatchesPage'
import { StandingsPage } from './pages/admin/StandingsPage'
import { LadderPage } from './pages/admin/LadderPage'
import { BracketPage } from './pages/admin/BracketPage'
import { KingPage } from './pages/admin/KingPage'
import { PresentationPage } from './pages/admin/PresentationPage'
import { HelpPage } from './pages/admin/HelpPage'
import { SponsorPage } from './pages/admin/SponsorPage'
import { LiveDesignPage } from './pages/admin/LiveDesignPage'
import { ReportsPage } from './pages/admin/ReportsPage'
import { TvPage } from './pages/public/TvPage'
import { PublicRefereePage } from './pages/public/PublicRefereePage'
import { TournamentSettingsPage } from './pages/admin/TournamentSettingsPage'
import { TestRunnerPage } from './pages/admin/TestRunnerPage'
import { PlatformLayout } from './layouts/PlatformLayout'
import { PlatformDashboard } from './pages/platform/PlatformDashboard'
import { PlatformSubscriptions } from './pages/platform/PlatformSubscriptions'
import { UpgradePage } from './pages/admin/UpgradePage'
import { PlatformMonitoring } from './pages/platform/PlatformMonitoring'
import { PlatformSettings } from './pages/platform/PlatformSettings'
import { PlatformPlaceholder } from './pages/platform/PlatformPlaceholder'
import { PlatformClients } from './pages/platform/PlatformClients'
import { PlatformTournaments } from './pages/platform/PlatformTournaments'
import { PlatformGuide } from './pages/platform/PlatformGuide'
import { PublicSignupPage } from './pages/public/PublicSignupPage'
import PublicLayout from './components/layout/PublicLayout'
import PublicLivePage from './pages/public/PublicLivePage'
import PublicMatchesPage from './pages/public/PublicMatchesPage'
import PublicStandingsPage from './pages/public/PublicStandingsPage'
import PublicMyTeamPage from './pages/public/PublicMyTeamPage'
import { AccessPage } from './pages/admin/AccessPage'
import { PrivacyPage } from './pages/public/PrivacyPage'
import { TermsPage } from './pages/public/TermsPage'
import { WkPouleLanding } from './pages/public/WkPouleLanding'
import { WkDashboard } from './pages/wkpoule/WkDashboard'
import { WkLeaguePage } from './pages/wkpoule/WkLeaguePage'
import { WkAdminPage } from './pages/admin/WkAdminPage'
import { WkInviteRedirect } from './pages/wkpoule/WkInviteRedirect'
import { EsportsScoutHQPage } from './pages/admin/EsportsScoutHQPage'


import { HelpProvider } from './contexts/HelpContext'
import { GlobalAudioListener } from './components/GlobalAudioListener'
import { PWAInstallBanner } from './components/tools/PWAInstallBanner'

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />
}

const WkProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>
  return user ? children : <Navigate to={`/login?redirectTo=${encodeURIComponent(location.pathname)}`} replace />
}

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth()
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>
  return user && user.email === 'pascal.teunissen@gmail.com' ? <Layout>{children}</Layout> : <Navigate to="/admin/dashboard" />
}

function App() {
  return (
    <BrowserRouter>
      <GlobalAudioListener />
      <PWAInstallBanner />
      <AuthProvider>
        <TenantProvider>
          <HelpProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/wkpoule" element={<WkPouleLanding />} />
              <Route path="/e-sports" element={<EsportsLandingPage />} />
              <Route path="/proracket" element={<RacketLandingPage />} />
              <Route path="/wkpoule/join/:inviteCode" element={<WkInviteRedirect />} />
              <Route path="/wkpoule/dashboard" element={
                <WkProtectedRoute><WkDashboard /></WkProtectedRoute>
              } />
              <Route path="/wkpoule/league/:id" element={
                <WkProtectedRoute><WkLeaguePage /></WkProtectedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute><ClubSettingsPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/teams" element={
                <ProtectedRoute><TeamsPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/players" element={
                <ProtectedRoute><PlayersPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/divisions" element={
                <ProtectedRoute><DivisionsPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/matches" element={
                <ProtectedRoute><MatchesPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/standings" element={
                <ProtectedRoute><StandingsPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/ladder" element={
                <ProtectedRoute><LadderPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/bracket" element={
                <ProtectedRoute><BracketPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/king" element={
                <ProtectedRoute><KingPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/presentation" element={
                <ProtectedRoute><PresentationPage /></ProtectedRoute>
              } />
              <Route path="/admin/help" element={
                <ProtectedRoute><HelpPage /></ProtectedRoute>
              } />
              <Route path="/admin/sponsors" element={
                <ProtectedRoute><SponsorPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/sponsors" element={
                <ProtectedRoute><SponsorPage /></ProtectedRoute>
              } />
              <Route path="/admin/wk-sync" element={
                <SuperAdminRoute><WkAdminPage /></SuperAdminRoute>
              } />
              <Route path="/admin/test-runner" element={
                <SuperAdminRoute><TestRunnerPage /></SuperAdminRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/reports" element={
                <ProtectedRoute><ReportsPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/settings" element={
                <ProtectedRoute><TournamentSettingsPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/design" element={
                <ProtectedRoute><LiveDesignPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/access" element={
                <ProtectedRoute><AccessPage /></ProtectedRoute>
              } />
              <Route path="/admin/tournaments/:tournamentId/scouthq" element={
                <ProtectedRoute><EsportsScoutHQPage /></ProtectedRoute>
              } />


              <Route path="/admin/upgrade" element={
                <ProtectedRoute><UpgradePage /></ProtectedRoute>
              } />

              <Route path="/live/:id/referee" element={<PublicRefereePage />} />
              <Route path="/live/:id/tv" element={<TvPage />} />

              <Route path="/join/:tournamentId" element={<PublicSignupPage />} />

              {/* Public Live Dashboard Routes */}
              <Route path="/live" element={<PublicLayout />}>
                <Route path=":id" element={<PublicLivePage />} />
                <Route path=":id/matches" element={<PublicMatchesPage />} />
                <Route path=":id/standings" element={<PublicStandingsPage />} />
                <Route path=":id/myteam" element={<PublicMyTeamPage />} />

              </Route>

              {/* Super Admin Platform Routes */}
              <Route path="/platform" element={<PlatformLayout />}>
                <Route index element={<PlatformDashboard />} />
                <Route path="dashboard" element={<PlatformDashboard />} />
                <Route path="subscriptions" element={<PlatformSubscriptions />} />
                <Route path="monitoring" element={<PlatformMonitoring />} />
                <Route path="settings" element={<PlatformSettings />} />
                <Route path="tenants" element={<PlatformClients />} />
                <Route path="tournaments" element={<PlatformTournaments />} />
                <Route path="docs" element={<PlatformGuide />} />
                <Route path="wkpoule" element={<WkAdminPage />} />
              </Route>

              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Routes>
          </HelpProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
